import { BasketCosts } from 'entities/compilationContext/OnsiteCompilationContext/Basket';
import { IAddress } from 'entities/compilationContext/address';
import { IDiscount } from 'entities/compilationContext/discount';
import { IPrice } from 'entities/compilationContext/price';
import { ITaxes } from 'entities/compilationContext/taxes';
import { ITravelAccommodationDate } from 'entities/compilationContext/travel-accommodation';
import { ITravelWhereWhen } from 'entities/compilationContext/travel-journey';

export const createPriceDefaultValues = (prefix: string): IPrice => {
  return {
    currency: prefix + '.currency',
    currencySymbol: prefix + '.currencySymbol',
    decimal: prefix + '.decimal',
    stripped: prefix + '.stripped',
    text: prefix + '.text',
  };
};

export const createBasketCostsDefaultValues = (prefix: string): BasketCosts => {
  return {
    total: createPriceDefaultValues(prefix + '.total'),
    subtotal: createPriceDefaultValues(prefix + '.subtotal'),
  };
};

export const createDiscountDefaultValues = (prefix: string): IDiscount => {
  return {
    cost: createPriceDefaultValues(prefix + '.cost'),
    label: prefix + '.label',
    code: prefix + '.code',
  };
};

export const createTaxDefaultValues = (prefix: string): ITaxes => {
  return {
    sales: {
      cost: createPriceDefaultValues(prefix + '.sales.cost'),
      label: prefix + '.sales.label',
    },
  };
};

export const createAddressDefaultValues = (prefix: string): IAddress => {
  return {
    line1: prefix + '.address.line1',
    line2: prefix + '.address.line2',
    city: prefix + '.address.city',
    country: prefix + '.address.country',
    county: prefix + '.address.county',
    postcode: prefix + '.address.postcode',
    freeText: prefix + '.address.freeText',
  };
};

export const createTravelAccommodationDatesDefaultValues = (prefix: string): ITravelAccommodationDate => {
  return {
    date: prefix + '.date',
    formattedDate: prefix + '.formattedDate',
    time: prefix + '.time',
    locale: prefix + '.locale',
    dateFormat: prefix + '.dateFormat',
  };
};

export const createTravelWhereWhenDefaultValues = (prefix: string): ITravelWhereWhen => {
  return {
    where: {
      iataCode: prefix + '.where.iataCode',
      value: prefix + '.where.value',
      label: prefix + '.where.label',
    },
    when: {
      date: prefix + '.when.date',
      formattedDate: prefix + '.when.formattedDate',
      time: prefix + '.when.time',
      locale: prefix + '.when.locale',
      dateFormat: prefix + '.when.dateFormat',
    },
  };
};

export const createValueLabelDefaultValues = (prefix: string) => {
  return {
    label: prefix + '.label',
    value: prefix + '.value',
  };
};

export const createProductDefaultValues = (prefix: string, removeDot?: boolean) => {
  // Removes the '.' for instances where product is the base-level property.
  const withDot = removeDot ? '' : '.';

  return {
    id: prefix + withDot + 'product.id',
    name: prefix + withDot + 'product.name',
    url: prefix + withDot + 'product.url',
    images: {
      thumbnail: prefix + withDot + 'product.images.thumbnail',
      additional: {
        'additional-id': prefix + withDot + 'product.images.additional[additional-id]',
      },
    },
    description: prefix + withDot + 'product.description',
    currency: prefix + withDot + 'product.currency',
    currencySymbol: prefix + withDot + 'product.currencySymbol',
    costs: {
      singleItem: createPriceDefaultValues(prefix + withDot + 'product.costs.singleItem'),
      previous: createPriceDefaultValues(prefix + withDot + 'product.costs.previous'),
      taxes: createTaxDefaultValues(prefix + withDot + 'product.costs.taxes'),
      discount: {
        cost: createPriceDefaultValues(prefix + withDot + 'product.costs.discount.cost'),
        label: prefix + withDot + 'product.costs.discount.label',
        code: prefix + withDot + 'product.costs.discount.code',
      },
      shipping: {
        address: createAddressDefaultValues(prefix + withDot + 'product.costs.shipping.address'),
        cost: createPriceDefaultValues(prefix + withDot + 'product.costs.singleItem'),
        label: prefix + withDot + 'product.costs.shipping.label',
        value: prefix + withDot + 'product.costs.shipping.value',
      },
      contract: {
        monthlyCost: createPriceDefaultValues(prefix + withDot + 'product.costs.contract.monthlyCost'),
        contractLength: prefix + withDot + 'product.costs.contract.contractLength',
        oneOffCost: createPriceDefaultValues(prefix + withDot + 'product.costs.contract.oneOffCost'),
        totalContractPrice: createPriceDefaultValues(prefix + withDot + 'product.costs.contract.totalContractPrice'),
      },
    },
    color: createValueLabelDefaultValues(prefix + withDot + 'product.color'),
    size: createValueLabelDefaultValues(prefix + withDot + 'product.size'),
    inStock: prefix + withDot + 'product.inStock',
    category: {
      id: prefix + withDot + 'product.category.id',
      hierarchy: [],
      label: prefix + withDot + 'product.category.label',
      value: prefix + withDot + 'product.category.value',
    },
    accommodation: {
      start: createTravelAccommodationDatesDefaultValues(prefix + withDot + 'product.accommodation'),
      end: createTravelAccommodationDatesDefaultValues(prefix + withDot + 'product.accommodation'),
      location: prefix + withDot + 'product.accommodation.location',
      name: prefix + withDot + 'product.accommodation.name',
      roomType: prefix + withDot + 'product.accommodation.roomType',
      numRooms: prefix + withDot + 'product.accommodation.numRooms',
      occupants: {
        adults: prefix + withDot + 'product.accommodation.occupants.adults',
        children: prefix + withDot + 'product.accommodation.occupants.children',
        infants: prefix + withDot + 'product.accommodation.occupants.infants',
      },
      duration: {
        nights: prefix + withDot + 'product.accommodation.duration.nights',
      },
      boardType: prefix + withDot + 'product.accommodation.boardType',
    },
    journey: [
      {
        start: createTravelWhereWhenDefaultValues(prefix + withDot + 'product.journey[0].start'),
        end: createTravelWhereWhenDefaultValues(prefix + withDot + 'product.journey[0].end'),
        direction: prefix + withDot + 'product.journey.[0].direction',
        duration: prefix + withDot + 'product.journey.[0].duration',
        flightName: prefix + withDot + 'product.journey.[0].flightName',
        flightNumber: prefix + withDot + 'product.journey.[0].flightNumber',
        passengers: {
          adults: prefix + withDot + 'product.journey.[0].passengers.adults',
          children: prefix + withDot + 'product.journey.[0].passengers.children',
          infants: prefix + withDot + 'product.journey.[0].passengers.infants',
        },
        type: prefix + withDot + 'product.journey.[0].type',
        grade: createValueLabelDefaultValues(prefix + withDot + 'product.journey[0].grade'),
        airline: {
          name: prefix + withDot + 'product.journey.[0].airline.name',
        },
      },
    ],
    brand: prefix + withDot + 'product.brand',
    metricId: prefix + withDot + 'product.metricId',
    clientProductId: prefix + withDot + 'product.clientProductId',
    rating: createValueLabelDefaultValues(prefix + withDot + 'product.rating'),
    ticket: {
      accessName: prefix + withDot + 'product.ticket.accessName',
      accessValue: prefix + withDot + 'product.ticket.accessValue',
      eventAddress: prefix + withDot + 'product.ticket.eventAddress',
      date: prefix + withDot + 'product.ticket.date',
      seatName: prefix + withDot + 'product.ticket.seatName',
      seatValue: prefix + withDot + 'product.ticket.seatValue',
      sectionName: prefix + withDot + 'product.ticket.sectionName',
      sectionValue: prefix + withDot + 'product.ticket.sectionValue',
    },
    isLowStock: prefix + withDot + 'product.isLowStock',
  };
};
